import { useRollbar } from '@rollbar/react';
import axios from 'axios';
import { useRouter } from 'next/router';
import { useRef, useState } from 'react';
import type { OwnHome } from '@/clients/pxe-backend/leads.types';
import { SUNRUN_URL } from '@/constants/baseUrls';
import { trackEvents } from '@/utils/analytics';
import {
  type AllowedAttributionParams,
  getAttributionParams,
  getCampaignID,
} from '@/utils/attribution/attribution';
import { getCapitalOneShoppingClickId } from '@/utils/capitalOneShopping/capitalOneShopping';
import { sha256Hex } from '@/utils/crypto';
import { getFirstTouchUrl } from '@/utils/firstTouchUrl/firstTouchUrl';
import { getCurrentLeadIdToken, refreshLeadIdToken } from '@/utils/leadIdToken';
import { formatPhoneNumberE164, trimPhone } from '@/utils/phone';
import { getXxTrustedFormCertUrl } from '@/utils/tcpaValidation';
import useEligibility, { EligibilityType } from './useEligibility';

export enum ReferralValue {
  YES = 'yes',
  NO = 'no',
}

interface LeadFormSubmitData extends AllowedAttributionParams {
  // Fields required for tcpa validation
  universal_leadid: string;
  xxTrustedFormCertUrl: string;

  // Standard lead form properties
  first_name?: string;
  last_name?: string;
  zip?: string;
  email?: string;
  phone_home?: string;
  property_ownership?: OwnHome | undefined;

  // Embedded Lead form properties
  electric_bill?: string;
  additionalServices?: string;
  Referral_web_form__c?: ReferralValue;

  // Capital One Shopping attribution
  capital_one_dmi_click_id?: string;

  // First-touch landing page URL (PXE-3496), Zeus-only, not synced to SF
  furl?: string;

  /** Scheduled callback (SF LEAD_FUTURE_CONTACT_DATE_TIME via Zeus). ISO 8601 UTC. */
  futureContactDateTime?: string;
  /** Must be true when futureContactDateTime is set or SFDC ignores the visitor time. */
  doNotOverrideFutureContactDateTime?: boolean;

  // Misc.
  _lead_id?: string; // Unique per lead submission ID for Meta ads optimization
}

export type LeadFormInputData = Omit<
  LeadFormSubmitData,
  | 'universal_leadid'
  | 'xxTrustedFormCertUrl'
  | 'capital_one_dmi_click_id'
  | 'furl'
>;

const sanitizeZeusData = (zeusData: LeadFormInputData): LeadFormInputData => {
  return {
    ...zeusData,
    phone_home: trimPhone(zeusData.phone_home),
    first_name: zeusData.first_name?.trim(),
    last_name: zeusData.last_name?.trim(),
  };
};

export function useSubmitLeadToZeus(
  gtmSubmitEventFormType: string = 'Standard Lead Form'
) {
  const { fetchEligibility } = useEligibility();
  const rollbar = useRollbar();
  const [leadSubmissionId] = useState<string>(() => crypto.randomUUID());
  const router = useRouter();
  const hasTrackedFormStarted = useRef<boolean>(false);

  async function submitLeadToZeus({
    zeusData,
    contentfulCampaignId,
    isEcomm = false,
    overrideCampaignId = false,
    honeypot = false,
  }: {
    zeusData: LeadFormInputData;
    contentfulCampaignId?: string;
    overrideCampaignId?: boolean;
    isEcomm?: boolean;
    honeypot?: boolean;
  }) {
    const attributionParams = getAttributionParams();
    const campid = getCampaignID({
      overrideCampaignId,
      isEcomm,
      contentfulCampaignId,
    });
    const sanitizedZeusData = sanitizeZeusData(zeusData);

    // Retrieve lead validation tokens
    const universal_leadid = getCurrentLeadIdToken();
    const xxTrustedFormCertUrl = getXxTrustedFormCertUrl();
    let capital_one_dmi_click_id = getCapitalOneShoppingClickId();

    if (attributionParams.c1_cid && !capital_one_dmi_click_id) {
      capital_one_dmi_click_id = attributionParams.c1_cid;
    }

    const furl = getFirstTouchUrl();

    // Adds attribution parameters and campid to zeus data
    const enhancedZeusData: LeadFormSubmitData = {
      ...sanitizedZeusData,
      universal_leadid,
      xxTrustedFormCertUrl,
      ...attributionParams,
      campid,
      _lead_id: leadSubmissionId,
      ...(capital_one_dmi_click_id && { capital_one_dmi_click_id }),
      ...(furl && { furl }),
    };

    const inService = enhancedZeusData.zip
      ? fetchEligibility(EligibilityType.WEBSITE, enhancedZeusData.zip)
      : false;

    trackEvents({
      event: 'Form Submitted',
      form_type: gtmSubmitEventFormType,
      ...(enhancedZeusData.property_ownership && {
        home_ownership: enhancedZeusData.property_ownership,
      }),
      referral_status:
        enhancedZeusData?.Referral_web_form__c === ReferralValue.YES
          ? 'Referred'
          : 'not referred',
      in_service: inService,
      honeypot,
      lead_submission_id: enhancedZeusData._lead_id,
      form_zip_code: zeusData.zip,
      ...(enhancedZeusData.electric_bill && {
        average_electric_bill: enhancedZeusData.electric_bill,
      }),
      leadsUserData: {
        ...(enhancedZeusData.email && {
          sha256_email_address: await sha256Hex(
            enhancedZeusData.email.toLowerCase().trim()
          ),
        }),
        ...(enhancedZeusData.phone_home && {
          sha256_phone_number: await sha256Hex(
            formatPhoneNumberE164(enhancedZeusData.phone_home)
          ),
        }),
      },
    });

    if (!enhancedZeusData.universal_leadid) {
      rollbar.error('Error: No leadId token was found during lead submission');
    }

    const zeusRes = await axios.post(`${SUNRUN_URL}/v1/lead/submit`, null, {
      params: enhancedZeusData,
    });

    rollbar.info('Zeus Lead Submitted', {
      hasLeadId: Boolean(enhancedZeusData.universal_leadid),
      initiator: gtmSubmitEventFormType,
      honeypot,
    });

    // After a form submit refresh the top level leadId token in case there are multiple forms on the page
    refreshLeadIdToken();

    return zeusRes.data;
  }

  function trackFormStarted() {
    if (hasTrackedFormStarted.current) return;

    trackEvents({
      event: 'form_start',
      form_type: gtmSubmitEventFormType,
      lead_submission_id: leadSubmissionId,
      page_url: router.asPath,
    });
    hasTrackedFormStarted.current = true;
  }

  return { submitLeadToZeus, trackFormStarted };
}
